import { Howl, Howler } from 'howler';

const STORAGE_KEY = 'cyber-bikes-audio';

interface AudioSettings {
  musicVolume: number;
  sfxVolume: number;
  isMuted: boolean;
}

class AudioManager {
  private music: Howl | null = null;
  private musicVolume = 0.5;
  private sfxVolume = 0.7;
  private isMuted = false;
  private sounds: Map<string, Howl> = new Map();

  constructor() {
    this.loadSettings();
  }

  private loadSettings(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const settings: AudioSettings = JSON.parse(saved);
        this.musicVolume = settings.musicVolume;
        this.sfxVolume = settings.sfxVolume;
        this.isMuted = settings.isMuted;
        Howler.mute(this.isMuted);
      }
    } catch {
      // Ignore localStorage errors
    }
  }

  private saveSettings(): void {
    try {
      const settings: AudioSettings = {
        musicVolume: this.musicVolume,
        sfxVolume: this.sfxVolume,
        isMuted: this.isMuted,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // Ignore localStorage errors
    }
  }

  public playMusic(src: string, loop = true): void {
    // Stop existing music
    if (this.music) {
      this.music.stop();
      this.music.unload();
    }

    this.music = new Howl({
      src: [src],
      loop,
      volume: this.musicVolume,
      html5: true, // Better for long audio files
      onloaderror: (_id, error) => {
        console.warn('Failed to load music:', error);
      },
    });

    this.music.play();
  }

  public stopMusic(): void {
    if (this.music) {
      this.music.stop();
    }
  }

  public pauseMusic(): void {
    if (this.music) {
      this.music.pause();
    }
  }

  public resumeMusic(): void {
    if (this.music && !this.music.playing()) {
      this.music.play();
    }
  }

  public setMusicVolume(vol: number): void {
    this.musicVolume = Math.max(0, Math.min(1, vol));
    if (this.music) {
      this.music.volume(this.musicVolume);
    }
    this.saveSettings();
  }

  public getMusicVolume(): number {
    return this.musicVolume;
  }

  public setSfxVolume(vol: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, vol));
    this.saveSettings();
  }

  public getSfxVolume(): number {
    return this.sfxVolume;
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    Howler.mute(this.isMuted);
    this.saveSettings();
    return this.isMuted;
  }

  public setMute(muted: boolean): void {
    this.isMuted = muted;
    Howler.mute(this.isMuted);
    this.saveSettings();
  }

  public isMutedState(): boolean {
    return this.isMuted;
  }

  public playSound(src: string, volume?: number): void {
    // Check if sound is already loaded
    let sound = this.sounds.get(src);

    if (!sound) {
      sound = new Howl({
        src: [src],
        volume: volume ?? this.sfxVolume,
      });
      this.sounds.set(src, sound);
    }

    sound.volume(volume ?? this.sfxVolume);
    sound.play();
  }

  public preloadSound(src: string): void {
    if (!this.sounds.has(src)) {
      const sound = new Howl({
        src: [src],
        preload: true,
      });
      this.sounds.set(src, sound);
    }
  }

  public destroy(): void {
    if (this.music) {
      this.music.unload();
      this.music = null;
    }

    this.sounds.forEach((sound) => sound.unload());
    this.sounds.clear();
  }
}

export const audioManager = new AudioManager();
